import { DesktopInput, InputState } from './DesktopInput';
import { MobileTouchHUD } from './MobileTouchHUD';

export class InputManager {
  public desktopInput: DesktopInput;
  public mobileHUD: MobileTouchHUD;
  public isMobile: boolean = false;
  public handbrakeLatched: boolean = true;

  constructor(uiRoot: HTMLElement) {
    this.desktopInput = new DesktopInput();
    this.mobileHUD = new MobileTouchHUD();

    // Cross-sync handbrake state
    this.desktopInput.onToggleHandbrake((latched) => {
      this.handbrakeLatched = latched;
      this.mobileHUD.setHandbrake(latched);
    });

    this.mobileHUD.onToggleHandbrake((latched) => {
      this.handbrakeLatched = latched;
      this.desktopInput.setHandbrake(latched);
    });

    // Cross-sync blinker state with strict mutual exclusion
    this.desktopInput.onBlinkerChange = (dir) => {
      this.blinker = dir;
      this.turnPeakReached = false;
      this.mobileHUD.setBlinker(dir);
    };

    this.mobileHUD.onBlinkerChange = (dir) => {
      this.blinker = dir;
      this.turnPeakReached = false;
      this.desktopInput.setBlinker(dir);
    };

    this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 900;
    if (this.isMobile) {
      this.mobileHUD.mount(uiRoot);
    }
  }

  public blinker: 'left' | 'right' | 'hazard' | 'off' = 'off';
  private turnPeakReached: boolean = false;

  public toggleHandbrake(forceValue?: boolean) {
    this.handbrakeLatched = forceValue !== undefined ? forceValue : !this.handbrakeLatched;
    this.desktopInput.setHandbrake(this.handbrakeLatched);
    this.mobileHUD.setHandbrake(this.handbrakeLatched);
  }

  public toggleBlinker(dir: 'left' | 'right' | 'hazard') {
    // Mutual exclusion: activating one indicator cancels the other immediately
    if (this.blinker === dir) {
      this.blinker = 'off';
    } else {
      this.blinker = dir;
    }
    this.turnPeakReached = false;
    this.desktopInput.setBlinker(this.blinker);
    this.mobileHUD.setBlinker(this.blinker);
  }

  public setBlinker(dir: 'left' | 'right' | 'hazard' | 'off') {
    this.blinker = dir;
    this.turnPeakReached = false;
    this.desktopInput.setBlinker(dir);
    this.mobileHUD.setBlinker(dir);
  }

  public cancelBlinker() {
    if (this.blinker === 'left' || this.blinker === 'right') {
      this.setBlinker('off');
    }
  }

  public update(deltaTime: number): InputState {
    this.desktopInput.update(deltaTime);
    if (this.isMobile) {
      this.mobileHUD.update(deltaTime);
    }

    // Merge inputs (prefer mobile touch if active, fallback to desktop keyboard)
    const m = this.mobileHUD.state;
    const d = this.desktopInput.state;

    const throttle = Math.max(d.throttle, m.throttle);
    const brake = Math.max(d.brake, m.brake);
    const steering = Math.abs(m.steering) > 0.02 ? m.steering : d.steering;
    const clutch = Math.max(d.clutch, m.clutch);
    const handbrake = this.handbrakeLatched;

    // Automatic Indicator Cancellation (StVO Blinkerrückstellung nach Abbiegen)
    if (this.blinker === 'left') {
      // In this coordinate system: steering left produces negative angle
      if (steering < -0.12) {
        this.turnPeakReached = true;
      } else if (this.turnPeakReached && steering > -0.04) {
        this.cancelBlinker();
      }
    } else if (this.blinker === 'right') {
      // Steering right produces positive angle
      if (steering > 0.12) {
        this.turnPeakReached = true;
      } else if (this.turnPeakReached && steering < 0.04) {
        this.cancelBlinker();
      }
    } else {
      this.turnPeakReached = false;
    }

    const blinker = this.blinker;
    const shoulderCheck = m.shoulderCheck || d.shoulderCheck;
    const mirrorLook = m.mirrorLook || d.mirrorLook;
    const cameraToggle = this.desktopInput.consumeCameraToggle() || this.mobileHUD.consumeCameraToggle();
    const horn = d.horn || m.horn;
    const engineRestart = this.desktopInput.consumeEngineRestart() || this.mobileHUD.consumeEngineRestart();
    const gearRequested = this.desktopInput.consumeGearRequest() || this.mobileHUD.consumeGearRequested();
    const gearShiftUp = this.desktopInput.consumeGearShiftUp() || this.mobileHUD.consumeGearShiftUp();
    const gearShiftDown = this.desktopInput.consumeGearShiftDown() || this.mobileHUD.consumeGearShiftDown();
    const seatbeltToggle = this.desktopInput.consumeSeatbeltToggle() || this.mobileHUD.consumeSeatbeltToggle();

    const seatbeltFastened = d.seatbeltFastened || m.seatbeltFastened;
    const adjustCockpitToggle = d.adjustCockpitToggle || m.adjustCockpitToggle;

    return {
      throttle,
      brake,
      steering,
      handbrake,
      clutch,
      blinker,
      shoulderCheck,
      mirrorLook,
      cameraToggle,
      horn,
      engineRestart,
      seatbeltToggle,
      seatbeltFastened,
      gearShiftUp,
      gearShiftDown,
      gearRequested,
      adjustCockpitToggle,
    };
  }
}
